import React, { useCallback, useMemo } from 'react';

import { useSnackbar } from 'notistack';

import { ContentCopy as ContentCopyIcon, Download as DownloadIcon } from '@mui/icons-material';
import { Button, ButtonProps, Stack, Tooltip } from '@mui/material';

import { payloadDownloadFilename, serializePayload } from '~/utils/societePayloadBuilder/buildPayload';
import type { PayloadBuilderState } from '~/utils/societePayloadBuilder/types';
import { isPayloadComplete } from '~/utils/societePayloadBuilder/validatePayload';

interface PayloadBuilderExportActionsProps {
  state: PayloadBuilderState;
  fullWidth?: boolean;
}

function ExportButton({
  disabled,
  disabledTitle,
  fullWidth,
  sx,
  ...buttonProps
}: ButtonProps & { disabledTitle: string; fullWidth?: boolean }) {
  return (
    <Tooltip title={disabled ? disabledTitle : ''}>
      <span style={fullWidth ? { width: '100%' } : undefined}>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          disabled={disabled}
          fullWidth={fullWidth}
          sx={sx}
          {...buttonProps}
        />
      </span>
    </Tooltip>
  );
}

export default function PayloadBuilderExportActions({ state, fullWidth = false }: PayloadBuilderExportActionsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const isComplete = useMemo(() => isPayloadComplete(state), [state]);
  const disabledTitle = 'Complete all required fields before copying or downloading';
  const buttonSx = fullWidth ? { flex: 1, minWidth: 0 } : undefined;

  const handleCopy = useCallback(async () => {
    if (!isComplete) return;
    try {
      await navigator.clipboard.writeText(serializePayload(state));
      enqueueSnackbar('Payload copied to clipboard', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to copy payload', { variant: 'error' });
    }
  }, [enqueueSnackbar, isComplete, state]);

  const handleDownload = useCallback(() => {
    if (!isComplete) return;
    const exportJson = serializePayload(state);
    const blob = new Blob([exportJson], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = payloadDownloadFilename(state);
    try {
      document.body.appendChild(anchor);
      anchor.click();
    } finally {
      anchor.remove();
      URL.revokeObjectURL(url);
    }
  }, [isComplete, state]);

  return (
    <Stack
      direction={fullWidth ? { xs: 'column', sm: 'row' } : 'row'}
      spacing={1}
      sx={{ width: fullWidth ? '100%' : undefined, flexWrap: fullWidth ? undefined : 'wrap' }}
      useFlexGap
    >
      <ExportButton
        disabled={!isComplete}
        disabledTitle={disabledTitle}
        fullWidth={fullWidth}
        startIcon={<ContentCopyIcon fontSize="small" />}
        onClick={handleCopy}
        sx={buttonSx}
      >
        Copy to clipboard
      </ExportButton>
      <ExportButton
        disabled={!isComplete}
        disabledTitle={disabledTitle}
        fullWidth={fullWidth}
        startIcon={<DownloadIcon fontSize="small" />}
        onClick={handleDownload}
        sx={buttonSx}
      >
        Download .txt
      </ExportButton>
    </Stack>
  );
}
